import { useState, useEffect } from "react";
import api, { fmtMoney, fmtDate, PAYMENT_LABELS } from "../lib/api";
import { StatCard } from "../components/StatCard";
import { PageHeader } from "../components/CrudPage";
import {
  Package, Boxes, ShoppingCart, CalendarRange, CalendarDays, TrendingUp,
  TrendingDown, PiggyBank, Coins, AlertTriangle, Clock,
} from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, LineChart, Line, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend,
} from "recharts";

const Panel = ({ title, children, testId }) => (
  <div className="border border-gray-200 bg-white" data-testid={testId}>
    <div className="border-b border-gray-200 px-5 py-3">
      <h3 className="font-heading text-sm font-semibold uppercase tracking-wider text-[#0B1F3A]">{title}</h3>
    </div>
    <div className="p-5">{children}</div>
  </div>
);

export default function Dashboard() {
  const [d, setD] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then((r) => setD(r.data)).catch(() => {});
  }, []);

  if (!d)
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-[#0B1F3A] border-t-[#D4AF37]" />
      </div>
    );

  return (
    <div data-testid="dashboard-page">
      <PageHeader title="Dashboard" subtitle="İşletmenizin genel durumu" />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard testId="stat-total-products" label="Toplam Ürün" value={d.total_products} icon={Package} />
        <StatCard testId="stat-total-stock" label="Toplam Stok" value={d.total_stock} icon={Boxes} />
        <StatCard testId="stat-today-sales" label="Bugünkü Satış" value={fmtMoney(d.today_sales)} icon={ShoppingCart} accent />
        <StatCard testId="stat-week-sales" label="Haftalık Satış" value={fmtMoney(d.week_sales)} icon={CalendarRange} />
        <StatCard testId="stat-month-sales" label="Aylık Satış" value={fmtMoney(d.month_sales)} icon={CalendarDays} />
        <StatCard testId="stat-total-income" label="Toplam Gelir" value={fmtMoney(d.total_income)} icon={TrendingUp} />
        <StatCard testId="stat-total-expense" label="Toplam Gider" value={fmtMoney(d.total_expense)} icon={TrendingDown} />
        <StatCard testId="stat-gross-profit" label="Brüt Kar" value={fmtMoney(d.gross_profit)} icon={PiggyBank} />
        <StatCard testId="stat-net-profit" label="Net Kar" value={fmtMoney(d.net_profit)} icon={Coins} accent />
        <StatCard testId="stat-today-net-profit" label="Bugünkü Net Kar" value={fmtMoney(d.today_net_profit)} icon={Coins} />
        <StatCard testId="stat-month-net-profit" label="Aylık Net Kar" value={fmtMoney(d.month_net_profit)} icon={Coins} />
        <StatCard testId="stat-low-stock" label="Düşük Stok" value={d.low_stock_count} icon={AlertTriangle}
          accent={d.low_stock_count > 0} sub="minimum seviyenin altında" />
      </div>

      <div className="mt-8 grid grid-cols-1 gap-6 xl:grid-cols-2">
        <Panel title="Aylık Satış Grafiği" testId="chart-monthly-sales">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={d.chart}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#6B7280" }} />
              <YAxis tick={{ fontSize: 12, fill: "#6B7280" }} />
              <Tooltip formatter={(v) => fmtMoney(v)} />
              <Bar dataKey="satis" name="Satış" fill="#0B1F3A" />
            </BarChart>
          </ResponsiveContainer>
        </Panel>
        <Panel title="Gelir / Gider Grafiği" testId="chart-income-expense">
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={d.chart}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#6B7280" }} />
              <YAxis tick={{ fontSize: 12, fill: "#6B7280" }} />
              <Tooltip formatter={(v) => fmtMoney(v)} />
              <Legend />
              <Line type="monotone" dataKey="gelir" name="Gelir" stroke="#0B1F3A" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="gider" name="Gider" stroke="#DC2626" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </Panel>
      </div>

      <div className="mt-6">
        <Panel title="Kar Grafiği" testId="chart-profit">
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={d.chart}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#6B7280" }} />
              <YAxis tick={{ fontSize: 12, fill: "#6B7280" }} />
              <Tooltip formatter={(v) => fmtMoney(v)} />
              <Line type="monotone" dataKey="kar" name="Net Kar" stroke="#D4AF37" strokeWidth={2.5} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </Panel>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Panel title="En Çok Satan Ürünler" testId="panel-top-products">
          {d.top_products.length === 0 ? <p className="text-sm text-gray-400">Henüz satış yok</p> : (
            <ul className="space-y-3">
              {d.top_products.map((p, i) => (
                <li key={i} className="flex items-center justify-between border-b border-gray-100 pb-2 text-sm last:border-0">
                  <span className="flex items-center gap-3">
                    <span className="flex h-6 w-6 items-center justify-center bg-[#0B1F3A] text-xs font-bold text-[#D4AF37]">{i + 1}</span>
                    {p.name}
                  </span>
                  <span className="text-gray-500">{p.quantity} adet · {fmtMoney(p.total)}</span>
                </li>
              ))}
            </ul>
          )}
        </Panel>
        <Panel title="Son Satışlar" testId="panel-recent-sales">
          {d.recent_sales.length === 0 ? <p className="text-sm text-gray-400">Henüz satış yok</p> : (
            <ul className="space-y-3">
              {d.recent_sales.map((s, i) => (
                <li key={i} className="flex items-center justify-between border-b border-gray-100 pb-2 text-sm last:border-0">
                  <div>
                    <p className="font-medium">{s.sale_no}</p>
                    <p className="text-xs text-gray-400">{fmtDate(s.created_at)} · {s.user_name}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-[#0B1F3A]">{fmtMoney(s.total)}</p>
                    <p className="text-xs text-gray-400">{PAYMENT_LABELS[s.payment_method]}</p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Panel>
        <Panel title="Düşük Stok Uyarıları" testId="panel-low-stock">
          {d.low_stock_products.length === 0 ? <p className="text-sm text-gray-400">Düşük stoklu ürün yok</p> : (
            <ul className="space-y-3">
              {d.low_stock_products.map((p, i) => (
                <li key={i} className="flex items-center justify-between border-b border-gray-100 pb-2 text-sm last:border-0">
                  <span className="flex items-center gap-2"><AlertTriangle size={16} className="text-[#D4AF37]" />{p.name}</span>
                  <span className="text-gray-500">{p.stock} / min {p.min_stock}</span>
                </li>
              ))}
            </ul>
          )}
        </Panel>
        <Panel title="Yaklaşan Randevular" testId="panel-upcoming-appointments">
          {d.upcoming_appointments.length === 0 ? <p className="text-sm text-gray-400">Yaklaşan randevu yok</p> : (
            <ul className="space-y-3">
              {d.upcoming_appointments.map((a, i) => (
                <li key={i} className="flex items-center justify-between border-b border-gray-100 pb-2 text-sm last:border-0">
                  <span className="flex items-center gap-2"><Clock size={16} className="text-[#0B1F3A]" />{a.name}</span>
                  <span className="text-gray-500">{a.date} {a.time}</span>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>
    </div>
  );
}
